import React, { useState, useEffect } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../constants/statusColors';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  WatchLater as WatchLaterIcon,
  CheckCircle as CheckCircleIcon,
  BadgeOutlined as EmiratesIdIcon,
  ArticleOutlined as PassportIcon,
  BusinessOutlined as TradeLicenseIcon,
  AssignmentOutlined as BoardResolutionIcon,
  DescriptionOutlined as CommercialRegIcon,
  GavelOutlined as PowerOfAttorneyIcon,
  DocumentScannerOutlined as OtherDocIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { useSnackbar } from 'notistack';
import StatCard from '../components/common/StatCard';
import FilterPanel from '../components/common/FilterPanel';
import DataTable from '../components/common/DataTable';
import { csAdminApi } from '../services/api';
import { mapExpiryAlertFromApi } from '../utils/apiMappers';

const ExpiryAlertsList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({
    overdue: 0,
    urgent: 0,
    warning: 0,
    valid: 0,
  });

  // Quick Update Modal
  const [showQuickUpdate, setShowQuickUpdate] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Filter Data
  const [companies, setCompanies] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingDirectors, setLoadingDirectors] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    companyId: '',
    directorId: '',
  });

  // Pagination & Sorting
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [sortBy, setSortBy] = useState('expiryDate');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (filters.companyId) {
      fetchDirectors(filters.companyId);
    } else {
      setDirectors([]);
      // Clear director filter when company is cleared
      if (filters.directorId) {
        setFilters(prev => ({ ...prev, directorId: '' }));
      }
    }
  }, [filters.companyId]);

  useEffect(() => {
    fetchAlerts();
  }, [searchQuery, filters, pagination.page, sortBy, sortOrder]);

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      {
        const response = await csAdminApi.getCompanies();
        const companyList = response.data?.data || [];
        setCompanies(companyList);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchDirectors = async (companyId) => {
    try {
      setLoadingDirectors(true);
      {
        const response = await csAdminApi.getDirectors({ company_id: companyId });
        const directorList = response.data?.data || [];
        setDirectors(directorList);
      }
    } catch (err) {
      console.error('Error fetching directors:', err);
      setDirectors([]);
    } finally {
      setLoadingDirectors(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);

      const params = {
          status: filters.status || undefined,
          company_id: filters.companyId ? Number(filters.companyId) : undefined,
          director_id: filters.directorId ? Number(filters.directorId) : undefined,
        };
        const response = await csAdminApi.getExpiryAlerts(params);
        let rows = (response.data?.data || []).map(mapExpiryAlertFromApi);

        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          rows = rows.filter(
            (a) =>
              a.documentType?.toLowerCase().includes(q) ||
              a.directorName?.toLowerCase().includes(q) ||
              a.companyName?.toLowerCase().includes(q) ||
              a.documentNumber?.toLowerCase().includes(q)
          );
        }

        if (filters.type) {
          const typeMap = {
            PASSPORT: 'Passport',
            EMIRATES_ID: 'Emirates ID',
            TRADE_LICENSE: 'Trade License',
            COMMERCIAL_LICENSE: 'Trade License',
            VISA: 'Visa',
            WORK_PERMIT: 'Work Permit',
            BOARD_RESOLUTION: 'Board Resolution',
            POWER_OF_ATTORNEY: 'Power of Attorney',
          };
          const label = typeMap[filters.type];
          if (label) {
            rows = rows.filter((a) => a.documentType === label);
          }
        }

        rows.sort((a, b) => {
          let aValue = a[sortBy];
          let bValue = b[sortBy];
          if (sortBy === 'document') {
            aValue = a.documentType;
            bValue = b.documentType;
          } else if (sortBy === 'name') {
            aValue = a.directorName;
            bValue = b.directorName;
          } else if (sortBy === 'expiryDate') {
            aValue = new Date(a.expiryDate).getTime();
            bValue = new Date(b.expiryDate).getTime();
          }
          if (sortOrder === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });

        const apiSummary = response.data?.summary || {
          overdue: rows.filter((a) => a.status === 'OVERDUE').length,
          urgent: rows.filter((a) => a.status === 'URGENT').length,
          warning: rows.filter((a) => a.status === 'WARNING').length,
          valid: rows.filter((a) => a.status === 'VALID').length,
        };

        setAlerts(rows);
        setSummary(apiSummary);
        setPagination((prev) => ({
          ...prev,
          total: rows.length,
          totalPages: Math.max(1, Math.ceil(rows.length / prev.limit)),
        }));
        setError('');
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setError('Failed to load expiry alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      type: '',
      status: '',
      companyId: '',
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleQuickUpdate = (alert) => {
    setSelectedAlert(alert);
    setNewExpiryDate(null);
    setUploadedFile(null);
    setShowQuickUpdate(true);
  };

  const formatExpiryForApi = (date) => {
    if (!date) return undefined;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleSubmitQuickUpdate = async () => {
    try {
      const payload = {
        expiryDate: formatExpiryForApi(newExpiryDate),
        director_id: selectedAlert.director_id,
        person_id: selectedAlert.person_id,
        doc_type: selectedAlert.document_type,
        source: selectedAlert.source,
        status: 'UPDATED',
      };
      await csAdminApi.updateExpiryAlert(
        selectedAlert.id,
        payload,
        uploadedFile || undefined
      );
      enqueueSnackbar('Alert updated successfully!', { variant: 'success' });
      setShowQuickUpdate(false);
      fetchAlerts();
    } catch (err) {
      enqueueSnackbar('Failed to update alert', { variant: 'error' });
    }
  };

  const getSeverityColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return '#D32F2F'; // Red
    if (daysUntilExpiry <= 30) return '#FF9800'; // Orange
    if (daysUntilExpiry <= 60) return '#FFC107'; // Yellow
    return ACTIVE_GREEN; // Green
  };

  // Helper function to get icon for document type
  const getDocumentIcon = (type) => {
    const iconProps = { sx: { fontSize: 20 } };
    switch (type) {
      case 'Emirates ID':
        return <EmiratesIdIcon {...iconProps} />;
      case 'Passport':
        return <PassportIcon {...iconProps} />;
      case 'Trade License':
        return <TradeLicenseIcon {...iconProps} />;
      case 'Board Resolution':
        return <BoardResolutionIcon {...iconProps} />;
      case 'Commercial Registration':
        return <CommercialRegIcon {...iconProps} />;
      case 'Power of Attorney':
        return <PowerOfAttorneyIcon {...iconProps} />;
      default:
        return <OtherDocIcon {...iconProps} />;
    }
  };

  // Helper function to get color for document type
  const getDocumentColor = (type) => {
    switch (type) {
      case 'Emirates ID':
        return '#2196F3';
      case 'Passport':
        return '#9C27B0';
      case 'Trade License':
        return ACTIVE_GREEN;
      case 'Board Resolution':
        return '#FF9800';
      case 'Commercial Registration':
        return '#00BCD4';
      case 'Power of Attorney':
        return '#795548';
      default:
        return '#757575';
    }
  };

  // Table columns
  const columns = [
    {
      id: 'document',
      label: 'Document',
      minWidth: 280,
      sortable: true,
      render: (row) => {
        const color = getDocumentColor(row.documentType);
        const severityColor = getSeverityColor(row.daysUntilExpiry);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '8px',
                bgcolor: `${color}15`,
                color: color,
                position: 'relative',
              }}
            >
              {getDocumentIcon(row.documentType)}
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: severityColor,
                  border: '2px solid white',
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.documentType}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {row.documentNumber}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'name',
      label: 'Director/Company',
      minWidth: 200,
      sortable: true,
      render: (row) => row.directorName || row.companyName,
    },
    {
      id: 'company',
      label: 'Company',
      minWidth: 180,
      sortable: true,
      render: (row) => row.companyName,
    },
    {
      id: 'expiryDate',
      label: 'Expiry Date',
      minWidth: 120,
      sortable: true,
      render: (row) => new Date(row.expiryDate).toLocaleDateString('en-GB'),
    },
    {
      id: 'daysUntilExpiry',
      label: 'Days Until Expiry',
      minWidth: 150,
      sortable: true,
      render: (row) => {
        const isOverdue = row.daysUntilExpiry < 0;
        return (
          <Typography
            variant="body2"
            sx={{
              color: getSeverityColor(row.daysUntilExpiry),
              fontWeight: 500,
            }}
          >
            {isOverdue ? `${Math.abs(row.daysUntilExpiry)} days overdue` : `${row.daysUntilExpiry} days`}
          </Typography>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      sortable: true,
      render: (row) => {
        const statusConfig = {
          OVERDUE: { bg: '#FFEBEE', color: '#C62828', label: 'DANGER' },
          URGENT: { bg: '#FFF3E0', color: '#E65100', label: 'WARNING' },
          WARNING: { bg: '#FFF3E0', color: '#E65100', label: 'WARNING' },
          VALID: { bg: ACTIVE_GREEN_BG, color: ACTIVE_GREEN, label: 'SUCCESS' },
        };
        const config = statusConfig[row.status] || { bg: '#E3F2FD', color: '#1976D2', label: 'INFO' };
        
        return (
          <Chip
            label={row.status}
            sx={{
              bgcolor: config.bg,
              color: config.color,
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 24,
              borderRadius: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
            size="small"
          />
        );
      },
    },
    {
      id: 'action',
      label: 'Action',
      minWidth: 110,
      sticky: 'right',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleQuickUpdate(row)}
          sx={{ borderRadius: '6px' }}
        >
          Update
        </Button>
      ),
    },
  ];

  const getStatusColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0) return 'error';
    if (daysUntilExpiry <= 30) return 'warning';
    return 'default';
  };

  const filterConfig = [
    {
      name: 'companyId',
      label: 'Company',
      value: filters.companyId,
      options: companies.map(c => ({
        value: String(c.companyId || c.company_id || c.id),
        label: c.companyName || c.company_name || c.name || 'Unknown Company'
      })),
      loading: loadingCompanies,
    },
    {
      name: 'directorId',
      label: 'Director',
      value: filters.directorId,
      options: directors.map(d => ({
        value: String(d.directorId || d.director_id || d.id),
        label: `${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`.trim() || 'Unknown Director'
      })),
      loading: loadingDirectors,
      disabled: !filters.companyId,
    },
    {
      name: 'type',
      label: 'Type',
      value: filters.type,
      options: [
        { value: 'PASSPORT', label: 'Passport' },
        { value: 'EMIRATES_ID', label: 'Emirates ID' },
        { value: 'COMMERCIAL_LICENSE', label: 'Commercial License' },
      ],
    },
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'OVERDUE', label: 'Overdue' },
        { value: 'URGENT', label: 'Urgent (< 30 days)' },
        { value: 'WARNING', label: 'Warning (< 60 days)' },
      ],
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Expiry Alerts
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Monitor and manage document and license expiries
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Overdue"
            value={summary.overdue}
            icon={ErrorIcon}
            color="#D32F2F"
            onClick={() => handleFilterChange('status', 'OVERDUE')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="< 30 Days"
            value={summary.urgent}
            icon={WarningIcon}
            color="#FF9800"
            onClick={() => handleFilterChange('status', 'URGENT')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="< 60 Days"
            value={summary.warning}
            icon={WatchLaterIcon}
            color="#FFC107"
            onClick={() => handleFilterChange('status', 'WARNING')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Valid"
            value={summary.valid}
            icon={CheckCircleIcon}
            color={ACTIVE_GREEN}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <FilterPanel
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      {/* Data Table */}
      {loading && alerts.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          columns={columns}
          rows={alerts}
          onSort={handleSort}
          sortBy={sortBy}
          sortOrder={sortOrder}
          pagination={pagination}
          onPageChange={(page) => setPagination({ ...pagination, page })}
        />
      )}

      {/* Quick Update Dialog */}
      <Dialog open={showQuickUpdate} onClose={() => setShowQuickUpdate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick Update: {selectedAlert?.documentType}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {selectedAlert?.directorName || selectedAlert?.companyName} - {selectedAlert?.documentType}
            </Typography>
            
            <DatePicker
              label="New Expiry Date"
              value={newExpiryDate}
              onChange={(newValue) => setNewExpiryDate(newValue)}
              inputFormat="dd/MM/yyyy"
              renderInput={(params) => <TextField {...params} fullWidth required />}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Upload New Document (Optional)
              </Typography>
              <input
                type="file"
                id="quick-upload"
                hidden
                accept="application/pdf,image/*"
                onChange={(e) => setUploadedFile(e.target.files[0])}
              />
              <label htmlFor="quick-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  fullWidth
                >
                  Choose File
                </Button>
              </label>
              {uploadedFile && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {uploadedFile.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuickUpdate(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitQuickUpdate}
            disabled={!newExpiryDate}
          >
            Update & Clear Alert
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ExpiryAlertsList;
